/**
 * @file: AudioMessageBubble.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Inline voice-message player. Tapping toggles play/pause (no external app);
 * playback progress fills the waveform (or a thin track for legacy messages
 * with no waveform), and the label counts elapsed time while playing. Only
 * one audio plays across the whole inbox at a time — starting one pauses
 * whatever was playing.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMessagePayloadAudio } from '@euler/model/chat';
import { Audio, AVPlaybackStatus } from 'expo-av';
import { Pause, Play } from 'lucide-react-native';
import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, TouchableOpacity, View } from 'react-native';

const stripHash = (url: string) => url.split('#')[0];

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * The single currently-playing sound across the inbox. Starting playback on
 * any bubble pauses this one first so two clips never overlap.
 */
let activePause: (() => Promise<void>) | null = null;

export const AudioMessageBubble: FC<{
  payload: ChatMessagePayloadAudio;
  isMine: boolean;
  /** Received + not yet played → show the red unread dot. */
  unread?: boolean;
  /** Fired once when the viewer first starts playing (clears the dot). */
  onFirstPlay?: () => void;
}> = memo(({ payload, isMine, unread, onFirstPlay }) => {
  const { rpx } = useResponsiveContext();
  const soundRef = useRef<Audio.Sound | null>(null);
  const pauseRef = useRef<() => Promise<void>>(async () => undefined);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [progress, setProgress] = useState(0); // 0..1

  const durationMs = payload.durationMs ?? 0;
  const waveform: number[] = payload.waveform ?? [];
  const hasWave = waveform.length > 0;
  const fg = isMine ? '#fff' : '#111';
  const dim = isMine ? 'rgba(255,255,255,0.4)' : 'rgba(17,17,17,0.25)';

  const onStatus = useCallback(
    (status: AVPlaybackStatus) => {
      if (!status.isLoaded) return;
      setPlaying(status.isPlaying);
      const dur = status.durationMillis || durationMs || 1;
      if (status.didJustFinish) {
        setPlaying(false);
        setPositionMs(0);
        setProgress(0);
        void soundRef.current?.setStatusAsync({
          positionMillis: 0,
          shouldPlay: false,
        });
        if (activePause === pauseRef.current) activePause = null;
        return;
      }
      setPositionMs(status.positionMillis);
      setProgress(Math.min(1, status.positionMillis / dur));
    },
    [durationMs],
  );

  const unload = useCallback(async () => {
    const s = soundRef.current;
    soundRef.current = null;
    if (activePause === pauseRef.current) activePause = null;
    if (s) {
      try {
        await s.unloadAsync();
      } catch {
        /* already gone */
      }
    }
  }, []);

  // Unload on unmount so a scrolled-away bubble releases the audio session.
  useEffect(() => {
    return () => {
      void unload();
    };
  }, [unload]);

  const toggle = useCallback(async () => {
    // Already loaded → just flip play/pause.
    const existing = soundRef.current;
    if (existing) {
      const st = await existing.getStatusAsync();
      if (st.isLoaded && st.isPlaying) {
        await existing.pauseAsync();
        if (activePause === pauseRef.current) activePause = null;
      } else {
        if (activePause && activePause !== pauseRef.current) {
          await activePause();
        }
        activePause = pauseRef.current;
        await existing.playAsync();
      }
      return;
    }

    // First play → load and start.
    setLoading(true);
    try {
      if (activePause) {
        await activePause();
        activePause = null;
      }
      await Audio.setAudioModeAsync({ playsInSilentModeIOS: true });
      const { sound } = await Audio.Sound.createAsync(
        { uri: stripHash(payload.url) },
        { shouldPlay: true, progressUpdateIntervalMillis: 100 },
        onStatus,
      );
      soundRef.current = sound;
      pauseRef.current = async () => {
        try {
          await sound.pauseAsync();
        } catch {
          /* unloaded */
        }
      };
      activePause = pauseRef.current;
      // First play → clear the received-unread red dot (server + local).
      onFirstPlay?.();
    } catch (e) {
      console.error('audio playback failed', e);
    } finally {
      setLoading(false);
    }
  }, [payload.url, onStatus, onFirstPlay]);

  const width = hasWave
    ? Math.min(rpx(260), rpx(110) + waveform.length * rpx(4))
    : Math.min(rpx(200), rpx(80) + Math.round(durationMs / 1000) * rpx(6));

  const activeBars = Math.round(progress * waveform.length);
  const showElapsed = playing || positionMs > 0;
  // Red unread dot on a received, not-yet-played voice (WeChat-style):
  // survives opening the thread, clears only on play.
  const showUnreadDot = !!unread && !isMine;

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={() => void toggle()}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(8),
          width,
          paddingHorizontal: rpx(14),
          paddingVertical: rpx(10),
          borderRadius: rpx(16),
          ...(isMine
            ? { borderTopRightRadius: rpx(4), backgroundColor: '#3b82f6' }
            : {
                borderTopLeftRadius: rpx(4),
                backgroundColor: 'rgb(223, 223, 223)',
              }),
        }}
      >
        {loading ? (
          <ActivityIndicator size="small" color={fg} />
        ) : playing ? (
          <Pause color={fg} size={rpx(18)} fill={fg} />
        ) : (
          <Play color={fg} size={rpx(18)} fill={fg} />
        )}

        {hasWave ? (
          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              height: rpx(24),
              gap: rpx(2),
            }}
          >
            {waveform.map((level, i) => (
              <View
                key={i}
                style={{
                  flex: 1,
                  height: Math.max(rpx(3), level * rpx(22)),
                  borderRadius: rpx(2),
                  backgroundColor: i < activeBars ? fg : dim,
                }}
              />
            ))}
          </View>
        ) : (
          <View
            style={{
              flex: 1,
              height: rpx(4),
              borderRadius: rpx(2),
              backgroundColor: dim,
              overflow: 'hidden',
            }}
          >
            <View
              style={{
                width: `${Math.round(progress * 100)}%`,
                height: '100%',
                backgroundColor: fg,
              }}
            />
          </View>
        )}

        <Typography.Text size={rpx(13)} color={fg}>
          {formatDuration(showElapsed ? positionMs : durationMs)}
        </Typography.Text>
      </TouchableOpacity>
      {showUnreadDot ? (
        <View
          style={{
            width: rpx(8),
            height: rpx(8),
            borderRadius: rpx(5),
            backgroundColor: '#ef4444',
            marginLeft: rpx(8),
          }}
        />
      ) : null}
    </View>
  );
});
